import { useState, useMemo, useCallback } from 'react'
import {
  Condition,
  ConditionCategory,
  ConditionStatus,
  SortField,
  SortDirection,
  ASSIGNEES,
  STATUSES,
  Document,
} from '../types/condition'
import { mockConditions } from '../data/mockConditions'

export function useConditions() {
  const [conditions, setConditions] = useState<Condition[]>(mockConditions)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeCategories, setActiveCategories] = useState<
    Set<ConditionCategory>
  >(new Set(['Income', 'Assets', 'Property', 'Credit', 'Insurance', 'Other']))
  const [activeAssignees, setActiveAssignees] = useState<Set<string>>(
    new Set(ASSIGNEES),
  )
  const [activeStatuses, setActiveStatuses] = useState<Set<ConditionStatus>>(
    new Set(STATUSES),
  )
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [sortField, setSortField] = useState<SortField>('dueDate')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  const filteredConditions = useMemo(() => {
    return conditions.filter((condition) => {
      const matchesSearch = condition.title
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
      const matchesCategory = activeCategories.has(condition.category)
      const matchesAssignee = activeAssignees.has(condition.assignee)
      const matchesStatus = activeStatuses.has(condition.status)
      return (
        matchesSearch && matchesCategory && matchesAssignee && matchesStatus
      )
    })
  }, [
    conditions,
    searchQuery,
    activeCategories,
    activeAssignees,
    activeStatuses,
  ])

  const sortedConditions = useMemo(() => {
    return [...filteredConditions].sort((a, b) => {
      let comparison = 0

      switch (sortField) {
        case 'title':
          comparison = a.title.localeCompare(b.title)
          break
        case 'dueDate':
          comparison =
            new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
          break
        case 'assignee':
          comparison = a.assignee.localeCompare(b.assignee)
          break
        case 'priority':
          const priorityOrder = { high: 0, medium: 1, low: 2 }
          comparison = priorityOrder[a.priority] - priorityOrder[b.priority]
          break
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })
  }, [filteredConditions, sortField, sortDirection])

  const conditionsByStatus = useMemo(() => {
    const grouped: Record<ConditionStatus, Condition[]> = {
      Pending: [],
      'In Progress': [],
      Submitted: [],
      Approved: [],
      Waived: [],
    }

    sortedConditions.forEach((condition) => {
      grouped[condition.status].push(condition)
    })

    return grouped
  }, [sortedConditions])

  const conditionsByCategory = useMemo(() => {
    const grouped: Record<ConditionCategory, Condition[]> = {
      Income: [],
      Assets: [],
      Property: [],
      Credit: [],
      Insurance: [],
      Other: [],
    }

    sortedConditions.forEach((condition) => {
      grouped[condition.category].push(condition)
    })

    return grouped
  }, [sortedConditions])

  const toggleCategory = useCallback((category: ConditionCategory) => {
    setActiveCategories((prev) => {
      const next = new Set(prev)
      if (next.has(category)) {
        next.delete(category)
      } else {
        next.add(category)
      }
      return next
    })
  }, [])

  const toggleAssignee = useCallback((assignee: string) => {
    setActiveAssignees((prev) => {
      const next = new Set(prev)
      if (next.has(assignee)) {
        next.delete(assignee)
      } else {
        next.add(assignee)
      }
      return next
    })
  }, [])

  const toggleStatus = useCallback((status: ConditionStatus) => {
    setActiveStatuses((prev) => {
      const next = new Set(prev)
      if (next.has(status)) {
        next.delete(status)
      } else {
        next.add(status)
      }
      return next
    })
  }, [])

  const setAllAssignees = useCallback((selected: boolean) => {
    setActiveAssignees(selected ? new Set(ASSIGNEES) : new Set())
  }, [])

  const setAllStatuses = useCallback((selected: boolean) => {
    setActiveStatuses(selected ? new Set(STATUSES) : new Set())
  }, [])

  const handleSort = useCallback((field: SortField) => {
    setSortField((prevField) => {
      if (prevField === field) {
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      } else {
        setSortDirection('asc')
      }
      return field
    })
  }, [])

  const moveCondition = useCallback(
    (conditionId: string, newStatus: ConditionStatus) => {
      setConditions((prev) =>
        prev.map((condition) =>
          condition.id === conditionId
            ? { ...condition, status: newStatus }
            : condition,
        ),
      )
    },
    [],
  )

  const addCondition = useCallback((newCondition: Omit<Condition, 'id'>) => {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    setConditions((prev) => [...prev, { ...newCondition, id }])
  }, [])

  const updateCondition = useCallback(
    (id: string, updates: Partial<Condition>) => {
      setConditions((prev) =>
        prev.map((condition) =>
          condition.id === id ? { ...condition, ...updates } : condition,
        ),
      )
    },
    [],
  )

  const updateConditionTitle = useCallback((id: string, title: string) => {
    setConditions((prev) =>
      prev.map((condition) =>
        condition.id === id ? { ...condition, title } : condition,
      ),
    )
  }, [])

  const updateConditionDueDate = useCallback((id: string, dueDate: string) => {
    setConditions((prev) =>
      prev.map((condition) =>
        condition.id === id ? { ...condition, dueDate } : condition,
      ),
    )
  }, [])

  const deleteCondition = useCallback((id: string) => {
    setConditions((prev) => prev.filter((condition) => condition.id !== id))
    setSelectedIds((prev) => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }, [])

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const selectAll = useCallback(
    (category?: ConditionCategory) => {
      const idsToSelect = category
        ? conditionsByCategory[category].map((c) => c.id)
        : sortedConditions.map((c) => c.id)
      setSelectedIds(new Set(idsToSelect))
    },
    [conditionsByCategory, sortedConditions],
  )

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  const bulkUpdateStatus = useCallback(
    (ids: string[], newStatus: ConditionStatus) => {
      setConditions((prev) =>
        prev.map((condition) =>
          ids.includes(condition.id)
            ? { ...condition, status: newStatus }
            : condition,
        ),
      )
      setSelectedIds(new Set())
    },
    [],
  )

  const bulkDelete = useCallback((ids: string[]) => {
    setConditions((prev) =>
      prev.filter((condition) => !ids.includes(condition.id)),
    )
    setSelectedIds(new Set())
  }, [])

  const addDocument = useCallback((conditionId: string, file: File) => {
    const newDocument: Document = {
      id: `doc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: file.name,
      type: file.type,
      size: file.size,
      uploadedAt: new Date().toISOString(),
      uploadedBy: 'Current User',
      url: URL.createObjectURL(file),
    }

    setConditions((prev) =>
      prev.map((condition) =>
        condition.id === conditionId
          ? { ...condition, documents: [...condition.documents, newDocument] }
          : condition,
      ),
    )
  }, [])

  const deleteDocument = useCallback(
    (conditionId: string, documentId: string) => {
      setConditions((prev) =>
        prev.map((condition) =>
          condition.id === conditionId
            ? {
                ...condition,
                documents: condition.documents.filter(
                  (doc) => doc.id !== documentId,
                ),
              }
            : condition,
        ),
      )
    },
    [],
  )

  const downloadAllDocuments = useCallback(
    (conditionId: string) => {
      const condition = conditions.find((c) => c.id === conditionId)
      if (!condition || condition.documents.length === 0) return

      // In a real application, this would create a zip file or download each document
      // For now, we'll simulate the download
      condition.documents.forEach((doc) => {
        // Simulate download by creating a temporary link
        const link = document.createElement('a')
        link.href = doc.url
        link.download = doc.name
        link.click()
      })

      // Show a notification (in a real app, you'd use a toast notification)
      console.log(
        `Downloading ${condition.documents.length} document(s) for: ${condition.title}`,
      )
    },
    [conditions],
  )

  return {
    conditions,
    conditionsByCategory,
    conditionsByStatus,
    searchQuery,
    setSearchQuery,
    activeCategories,
    toggleCategory,
    activeAssignees,
    toggleAssignee,
    setAllAssignees,
    activeStatuses,
    toggleStatus,
    setAllStatuses,
    sortField,
    sortDirection,
    handleSort,
    moveCondition,
    addCondition,
    updateCondition,
    updateConditionTitle,
    updateConditionDueDate,
    deleteCondition,
    totalCount: conditions.length,
    selectedIds,
    toggleSelection,
    selectAll,
    clearSelection,
    bulkUpdateStatus,
    bulkDelete,
    addDocument,
    deleteDocument,
    downloadAllDocuments,
  }
}
